import { BusinessRole } from '@mondaygpt/constants'
import { Button } from 'components/Button'
import { Checkbox } from 'components/Checkbox'
import { ConfirmDialog } from 'components/ConfirmDialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/Dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  dropdownItemDarkClass,
} from 'components/DropdownMenu'
import { cn } from 'lib/utils'
import { EmptyState } from 'components/EmptyState'
import { Input } from 'components/Input'
import { Label } from 'components/Label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/Select'
import { StatusPill } from 'components/StatusPill'
import { useCopyToClipboard } from 'hooks/use-copy-to-clipboard'
import { useIsMobile } from 'hooks'
import { Check, Copy, KeyRound, Loader2, MoreHorizontal, RefreshCw, Trash2 } from 'lucide-react'
import { selectCurrentBusinessRole } from 'modules/common/selectors'
import { useAppSelector } from 'modules/core'
import { ApiError, OpenAPI } from 'openapi'
import type { BusinessApiKeyDto } from 'openapi/models/BusinessApiKeyDto'
import type { BusinessApiKeyScopeInfoDto } from 'openapi/models/BusinessApiKeyScopeInfoDto'
import type { CreateBusinessApiKeyDto } from 'openapi/models/CreateBusinessApiKeyDto'
import { ApiKeysService } from 'openapi/services/ApiKeysService'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { toast } from 'sonner'
import { MobilePageHeader } from '../creative/components/MobilePageHeader'

type ScopeValue = CreateBusinessApiKeyDto['scopes'][number]

const EXPIRY_OPTIONS = [
  { value: 'never', label: 'Never expires' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
  { value: '365', label: '1 year' },
] as const

const KEY_PLACEHOLDER = 'taka_live_...'

function mcpConfigSnippet(base: string, key: string): string {
  return JSON.stringify(
    {
      mcpServers: {
        taka: {
          type: 'http',
          url: `${base}/mcp`,
          headers: { Authorization: `Bearer ${key}` },
        },
      },
    },
    null,
    2
  )
}

function curlSnippet(base: string, key: string): string {
  return `curl ${base}/v1/public/account \\\n  -H "Authorization: Bearer ${key}"`
}

function formatDate(value?: string | null): string {
  if (!value) return 'Never'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? 'Never' : date.toLocaleDateString()
}

/** True when the key's own expiry lands inside the 24h rotation grace window. */
function expiresWithinGrace(key: BusinessApiKeyDto | null): boolean {
  if (!key?.expiresAt) return false
  const expires = new Date(key.expiresAt).getTime()
  return !Number.isNaN(expires) && expires < Date.now() + 24 * 60 * 60 * 1000
}

/**
 * The server caps the rotation grace at the old key's own expiry, and the
 * replacement inherits that expiry; promising a flat 24 hours here would be
 * a lie for keys expiring sooner.
 */
function rotateGraceDescription(key: BusinessApiKeyDto | null): string {
  if (expiresWithinGrace(key) && key) {
    return `A new key is issued with the same scopes and the same expiry (${formatDate(key.expiresAt)}). The old key keeps working until that expiry, which is sooner than the usual 24 hour grace.`
  }
  return 'A new key is issued with the same scopes. The old key keeps working for 24 hours so you can swap it in your integrations, then it stops.'
}

function keyStatus(key: BusinessApiKeyDto): {
  label: string
  tone: 'published' | 'failed' | 'neutral' | 'pending'
} {
  if (key.status === 'revoked') return { label: 'Revoked', tone: 'failed' }
  if (key.isExpired) return { label: 'Expired', tone: 'neutral' }
  // A rotated-away key (24h grace) or one nearing its expiry: without this,
  // the old and new key both read "Active" and nothing says which one dies.
  if (key.expiresAt && new Date(key.expiresAt).getTime() < Date.now() + 48 * 60 * 60 * 1000) {
    return { label: 'Expiring soon', tone: 'pending' }
  }
  return { label: 'Active', tone: 'published' }
}

/** Multi-line copyable snippet block: mono pre + a small tertiary copy button. */
function SnippetBlock({ label, text }: { label: string; text: string }) {
  const { copied, copy } = useCopyToClipboard()
  const handleCopy = async () => {
    if (!(await copy(text, label))) {
      toast.error('Copy failed. Select the text and copy it manually.')
    }
  }
  return (
    // min-w-0: inside a grid/flex parent (DialogContent) the pre's intrinsic
    // width would otherwise widen the whole dialog past the viewport instead
    // of scrolling inside its own overflow-x-auto box.
    <div className="max-w-full min-w-0">
      <div className="mb-2 flex items-center justify-between">
        <span className="mono text-[10px] tracking-[0.18em] text-paper-faint uppercase">
          {label}
        </span>
        <Button variant="t3-ghost" size="t3-sm" onClick={handleCopy}>
          {copied === label ? <Check size={14} /> : <Copy size={14} />}
          {copied === label ? 'Copied' : 'Copy'}
        </Button>
      </div>
      <pre className="overflow-x-auto rounded-[9px] border border-line bg-bg-3 p-3 text-[12px] leading-relaxed text-paper-dim">
        <code>{text}</code>
      </pre>
    </div>
  )
}

/**
 * Business API key management plus connect instructions for the public API
 * and the MCP server. Keys act as the member who created them, scoped per
 * key; the plain key is shown exactly once at create/rotate time.
 */
export function ApiKeysPage() {
  const businessRole = useAppSelector(selectCurrentBusinessRole)
  const isMobile = useIsMobile()
  const canManage = businessRole === BusinessRole.Owner || businessRole === BusinessRole.Admin

  const [keys, setKeys] = useState<BusinessApiKeyDto[]>([])
  const [scopes, setScopes] = useState<BusinessApiKeyScopeInfoDto[]>([])
  const [loading, setLoading] = useState(true)
  const [forbidden, setForbidden] = useState(false)
  const [loadError, setLoadError] = useState(false)

  const [createOpen, setCreateOpen] = useState(false)
  const [createName, setCreateName] = useState('')
  const [createScopes, setCreateScopes] = useState<ScopeValue[]>([])
  const [createExpiry, setCreateExpiry] = useState<string>('never')
  const [creating, setCreating] = useState(false)
  const [createError, setCreateError] = useState('')

  const [revealKey, setRevealKey] = useState<string | null>(null)
  const [rotateTarget, setRotateTarget] = useState<BusinessApiKeyDto | null>(null)
  const [revokeTarget, setRevokeTarget] = useState<BusinessApiKeyDto | null>(null)

  const { copied, copy } = useCopyToClipboard()

  const apiBase = useMemo(() => String(OpenAPI.BASE || '').replace(/\/$/, ''), [])

  const fetchAll = useCallback(async () => {
    try {
      const [list, scopeInfo] = await Promise.all([
        ApiKeysService.apiKeysControllerListV1(),
        ApiKeysService.apiKeysControllerGetScopesV1(),
      ])
      setKeys(list.apiKeys)
      setScopes(scopeInfo.scopes)
      setLoadError(false)
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setForbidden(true)
      } else {
        console.error('Failed to load API keys:', err)
        // A standing flag, not just a transient toast: without it the page
        // falls into "No API keys yet" with a create dialog whose scope list
        // never loaded, a dishonest state and a dead end.
        setLoadError(true)
      }
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const resetCreateForm = () => {
    setCreateName('')
    setCreateScopes([])
    setCreateExpiry('never')
    setCreateError('')
  }

  const handleCreate = async () => {
    const name = createName.trim()
    if (!name || createScopes.length === 0) return
    setCreating(true)
    setCreateError('')
    try {
      const expiresAt =
        createExpiry === 'never'
          ? undefined
          : new Date(Date.now() + Number(createExpiry) * 24 * 60 * 60 * 1000).toISOString()
      const response = await ApiKeysService.apiKeysControllerCreateV1({
        name,
        scopes: createScopes,
        expiresAt,
      })
      setCreateOpen(false)
      resetCreateForm()
      setRevealKey(response.plainKey)
      fetchAll()
    } catch (err: any) {
      const raw = err?.body?.message
      setCreateError(
        (Array.isArray(raw) ? raw.join(' ') : raw) || err?.message || 'Failed to create the key'
      )
    } finally {
      setCreating(false)
    }
  }

  const handleRotate = async (key: BusinessApiKeyDto) => {
    const response = await ApiKeysService.apiKeysControllerRotateV1(key.id, {})
    setRevealKey(response.plainKey)
    toast.success(
      expiresWithinGrace(key)
        ? `Key rotated. The old key works until it expires on ${formatDate(key.expiresAt)}.`
        : 'Key rotated. The old key works for 24 more hours.'
    )
    fetchAll()
  }

  const handleRevoke = async (key: BusinessApiKeyDto) => {
    await ApiKeysService.apiKeysControllerRevokeV1(key.id)
    toast.success('Key revoked.')
    fetchAll()
  }

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-paper-faint" />
      </div>
    )
  }

  const createButton = (size: 't3-sm' | 't3-md') =>
    canManage && !forbidden && !loadError && keys.length > 0 ? (
      <Button variant="t3-primary" size={size} onClick={() => setCreateOpen(true)}>
        <KeyRound size={size === 't3-sm' ? 14 : 16} />
        Create key
      </Button>
    ) : undefined

  return (
    <div
      className={
        isMobile
          ? 'flex h-full flex-col overflow-y-auto pb-6'
          : 'mx-auto w-full max-w-[840px] px-6 py-8'
      }
    >
      {isMobile ? (
        <MobilePageHeader
          title="API & MCP"
          subtitle="Drive your Taka account from code or an AI assistant."
          actions={createButton('t3-sm')}
        />
      ) : (
        <div className="mb-6 flex items-center justify-between">
          <div>
            <span className="mono text-[10px] tracking-[0.2em] text-paper-faint uppercase">
              Developers
            </span>
            <h1 className="mt-1 text-[24px] font-semibold tracking-[-0.01em] text-paper sm:text-[28px]">
              API & MCP
            </h1>
            <p className="mt-1 text-[14px] text-paper-dim">
              Drive your Taka account from code or an AI assistant.
            </p>
          </div>
          {createButton('t3-md')}
        </div>
      )}

      <div className={isMobile ? 'space-y-6 px-4 pt-4' : 'space-y-6'}>
        {loadError ? (
          <div className="card p-6">
            <p className="text-[14px] font-medium text-paper">Could not load your API keys.</p>
            <p className="mt-1 text-[13px] text-paper-dim">
              This is on our side or the network, not your keys. Try again.
            </p>
            <Button
              variant="t3-tertiary"
              size="t3-md"
              className="mt-4"
              onClick={() => {
                setLoading(true)
                fetchAll()
              }}
            >
              Try again
            </Button>
          </div>
        ) : forbidden ? (
          <div className="card p-6">
            <p className="text-[14px] font-medium text-paper">
              API keys are managed by owners and admins.
            </p>
            <p className="mt-1 text-[13px] text-paper-dim">
              Ask a business owner to create a key for you, or to make you an admin.
            </p>
          </div>
        ) : keys.length === 0 ? (
          <div className="rounded-[12px] border border-dashed border-line bg-bg-2 py-12">
            <EmptyState
              icon={<KeyRound size={24} />}
              title="No API keys yet."
              lede="A key lets your code, or an AI assistant over MCP, act on this business. Each key gets its own scopes and can be revoked any time."
              primaryAction={
                canManage ? (
                  <Button variant="t3-primary" size="t3-md" onClick={() => setCreateOpen(true)}>
                    <KeyRound size={16} />
                    Create your first key
                  </Button>
                ) : undefined
              }
            />
          </div>
        ) : (
          <div className="overflow-x-auto rounded-[12px] border border-line bg-bg-2">
            <table className="w-full min-w-[640px]">
              <thead>
                <tr className="border-b border-line bg-bg-3">
                  {['Name', 'Key', 'Scopes', 'Status', 'Last used', ''].map((header, i) => (
                    <th
                      key={header || `col-${i}`}
                      className={
                        header
                          ? 'mono px-4 py-3 text-left text-[10px] tracking-[0.18em] text-paper-faint uppercase'
                          : 'w-10 px-4 py-3'
                      }
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {keys.map(key => {
                  const status = keyStatus(key)
                  return (
                    <tr key={key.id} className="transition-colors hover:bg-bg-3/60">
                      <td className="px-4 py-3">
                        <p className="truncate text-[13px] font-medium text-paper">{key.name}</p>
                        {key.createdByName && (
                          <p className="truncate text-[11px] text-paper-faint">
                            by {key.createdByName}
                          </p>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <span className="mono text-[12px] text-paper-dim">
                          {key.keyPrefix}…{key.last4}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-[12px] text-paper-dim" title={key.scopes.join(', ')}>
                          {key.scopes.length <= 2
                            ? key.scopes.join(', ')
                            : `${key.scopes.slice(0, 2).join(', ')} +${key.scopes.length - 2}`}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <StatusPill
                          tone={status.tone}
                          title={key.expiresAt ? `Expires ${formatDate(key.expiresAt)}` : undefined}
                        >
                          {status.label}
                        </StatusPill>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-[12px] text-paper-dim">
                          {formatDate(key.lastUsedAt)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {canManage && key.status === 'active' && !key.isExpired && (
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                aria-label="Key actions"
                                className="rounded-[6px] p-1 text-paper-faint transition-colors hover:bg-bg-3 hover:text-paper"
                              >
                                <MoreHorizontal size={16} />
                              </button>
                            </DropdownMenuTrigger>
                            {/* variant="dark": Radix portals outside the
                                .taka-dark wrapper, so the default popover
                                palette would render light-on-dark here. */}
                            <DropdownMenuContent variant="dark" align="end">
                              <DropdownMenuItem
                                className={cn('cursor-pointer', dropdownItemDarkClass)}
                                onClick={() => setRotateTarget(key)}
                              >
                                <RefreshCw size={14} />
                                Rotate
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                className="cursor-pointer text-[color:var(--alert-error-text)] data-[highlighted]:bg-bg-2 data-[highlighted]:text-[color:var(--alert-error-text)]"
                                onClick={() => setRevokeTarget(key)}
                              >
                                <Trash2 size={14} />
                                Revoke
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Connect instructions. Static snippets carry a placeholder key; the
            one-time reveal dialog embeds the real key right after minting. */}
        <div className="card space-y-4 p-6">
          <div>
            <h2 className="text-[16px] font-semibold text-paper">Connect an AI assistant (MCP)</h2>
            <p className="mt-1 text-[13px] text-paper-dim">
              Paste this into your MCP client (Claude, Cursor, and friends) and replace the key.
              Your assistant can then plan, generate, schedule, and publish for this business,
              within the key's scopes.
            </p>
          </div>
          <SnippetBlock label="mcp.config.json" text={mcpConfigSnippet(apiBase, KEY_PLACEHOLDER)} />
        </div>

        <div className="card space-y-4 p-6">
          <div>
            <h2 className="text-[16px] font-semibold text-paper">REST API</h2>
            <p className="mt-1 text-[13px] text-paper-dim">
              Same capabilities over plain HTTP. Docs live at{' '}
              <a
                className="text-paper underline decoration-[color:var(--line-bright)] underline-offset-2"
                href={`${apiBase}/docs`}
                target="_blank"
                rel="noreferrer"
              >
                {apiBase.replace(/^https?:\/\//, '')}/docs
              </a>
              , the OpenAPI spec at /openapi.json, and an agent-readable overview at /llms.txt.
              Guides and recipes:{' '}
              <a
                className="text-paper underline decoration-[color:var(--line-bright)] underline-offset-2"
                href="https://taka.ai/developers"
                target="_blank"
                rel="noreferrer"
              >
                taka.ai/developers
              </a>
              .
            </p>
          </div>
          <SnippetBlock label="curl" text={curlSnippet(apiBase, KEY_PLACEHOLDER)} />
        </div>
      </div>

      {/* Create dialog */}
      <Dialog
        open={createOpen}
        onOpenChange={open => {
          setCreateOpen(open)
          if (!open) resetCreateForm()
        }}
      >
        <DialogContent size="sm" variant="dark">
          <DialogHeader>
            <DialogTitle variant="dark">Create API key</DialogTitle>
            <DialogDescription variant="dark">
              The key acts as you, inside this business, limited to the scopes you pick.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input
                variant="dark"
                placeholder="e.g. Zapier, my agent, staging"
                value={createName}
                onChange={e => {
                  setCreateName(e.target.value)
                  setCreateError('')
                }}
              />
            </div>

            <div className="space-y-2">
              <Label>Scopes</Label>
              <div className="max-h-[240px] space-y-1 overflow-y-auto rounded-[9px] border border-line p-2">
                {scopes.map(scope => {
                  const checked = createScopes.includes(scope.scope as ScopeValue)
                  return (
                    <label
                      key={scope.scope}
                      className="flex cursor-pointer items-start gap-3 rounded-[8px] px-2 py-2 transition-colors hover:bg-bg-3"
                    >
                      <Checkbox
                        checked={checked}
                        onCheckedChange={value => {
                          setCreateScopes(prev =>
                            value
                              ? [...prev, scope.scope as ScopeValue]
                              : prev.filter(s => s !== scope.scope)
                          )
                        }}
                        className="mt-0.5"
                      />
                      <span className="min-w-0">
                        <span className="mono block text-[12px] text-paper">{scope.scope}</span>
                        <span className="block text-[12px] text-paper-dim">
                          {scope.description}
                        </span>
                      </span>
                    </label>
                  )
                })}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Expiry</Label>
              <Select value={createExpiry} onValueChange={setCreateExpiry}>
                <SelectTrigger variant="dark" className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent variant="dark">
                  {EXPIRY_OPTIONS.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {createError && (
              <p className="text-sm text-[color:var(--alert-error-text)]">{createError}</p>
            )}
          </div>

          <DialogFooter>
            <Button variant="t3-tertiary" size="t3-md" onClick={() => setCreateOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="t3-primary"
              size="t3-md"
              onClick={handleCreate}
              disabled={creating || !createName.trim() || createScopes.length === 0}
            >
              {creating && <Loader2 size={16} className="animate-spin" />}
              Create key
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* One-time reveal dialog (create + rotate land here) */}
      <Dialog open={revealKey !== null} onOpenChange={open => !open && setRevealKey(null)}>
        <DialogContent size="sm" variant="dark">
          <DialogHeader>
            <DialogTitle variant="dark">Your new API key</DialogTitle>
            <DialogDescription variant="dark">
              Shown once. Copy it now and store it somewhere safe; Taka keeps only a hash.
            </DialogDescription>
          </DialogHeader>

          {revealKey && (
            <div className="min-w-0 space-y-4 py-2">
              <div className="flex items-center gap-2">
                <div
                  className="mono min-w-0 flex-1 truncate rounded-[10px] border border-line bg-bg-3 px-4 py-3.5 text-[13px] text-paper"
                  title={revealKey}
                >
                  {revealKey}
                </div>
                <Button
                  variant="t3-primary"
                  size="t3-md"
                  onClick={async () => {
                    // The plain key exists ONLY here; a silent copy failure
                    // means the user closes the dialog believing it saved.
                    if (!(await copy(revealKey, 'key'))) {
                      toast.error('Copy failed. Select the key text and copy it manually.')
                    }
                  }}
                >
                  {copied === 'key' ? <Check size={16} /> : <Copy size={16} />}
                  {copied === 'key' ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <SnippetBlock
                label="ready-to-paste mcp config"
                text={mcpConfigSnippet(apiBase, revealKey)}
              />
            </div>
          )}

          <DialogFooter>
            <Button variant="t3-tertiary" size="t3-md" onClick={() => setRevealKey(null)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rotate confirm */}
      <ConfirmDialog
        open={rotateTarget !== null}
        onOpenChange={open => !open && setRotateTarget(null)}
        title="Rotate this key?"
        description={rotateGraceDescription(rotateTarget)}
        confirmLabel="Rotate key"
        pendingLabel="Rotating…"
        onConfirm={async () => {
          if (!rotateTarget) return
          try {
            await handleRotate(rotateTarget)
            setRotateTarget(null)
          } catch (err) {
            console.error('Failed to rotate API key:', err)
            toast.error('Could not rotate the key. Try again.')
            throw err
          }
        }}
      />

      {/* Revoke confirm */}
      <ConfirmDialog
        open={revokeTarget !== null}
        onOpenChange={open => !open && setRevokeTarget(null)}
        title="Revoke this key?"
        description="The key stops working immediately and anything using it starts failing. This cannot be undone."
        confirmLabel="Revoke key"
        pendingLabel="Revoking…"
        onConfirm={async () => {
          if (!revokeTarget) return
          try {
            await handleRevoke(revokeTarget)
            setRevokeTarget(null)
          } catch (err) {
            console.error('Failed to revoke API key:', err)
            toast.error('Could not revoke the key. Try again.')
            throw err
          }
        }}
      />
    </div>
  )
}
